import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Archive, ArchiveRestore, Edit2, Search, Printer, FileText, ShieldCheck, Ban, Fuel, Ship, Lock } from 'lucide-react';
import { PageShell, primaryButton, ghostButton, inputStyle, glassLight, T, Badge, StatCard, EmptyState, Field, SectionTitle, usePagination, Pagination, useIsMobile, CampoValidado, useConfirmacion, useDesplazarAlAbrir } from '../ui';
import { COMPARTIMIENTOS, PRODUCTOS_WOP, SUPLIDORES, TIPOS_DISPONIBLES, esWOP, etiquetaTipo, fmtFecha, fmtGal, imprimirPedido, normalizarTipo, nuevoToken, siguienteNumeroOrden } from './pedidoDocumento';
import { diferencias, type Campos } from '../auditoria';
import type { DetalleAuditoria } from '../db';
import { correoConfigurado, enviarAvisoPedido, type EventoCorreo } from '../email';
import type { Camion, Cola, Conductor, Envasadora, Pedido, PedidoLinea, PedidoTipo, ProductoWOP } from '../types';

// ── QUÉ SE APUNTA EN LA AUDITORÍA DE UNA ORDEN ──────────────────────────────
// El detalle se lee mucho después de firmada la orden, así que se guarda ya
// legible: el suplidor con su etiqueta, los destinos por nombre y el total en
// galones con su separador de miles.
const CAMPOS_PEDIDO: Campos = {
  suplidor: 'Suplidor',
  fecha: 'Fecha',
  conductor: 'Conductor',
  pase: 'Pase del chofer',
  cola: 'Ficha de la cola',
  placa: 'Placa del camión',
  destinos: 'Destinos',
  galones: 'Total de galones',
  observaciones: 'Observaciones'
};
const vistaPedido = (p?: Pedido) => p && {
  suplidor: etiquetaTipo(p.tipo),
  fecha: p.fecha,
  conductor: p.conductor,
  pase: p.paseChofer,
  cola: p.fichaCola,
  placa: p.placaCamion,
  destinos: (p.lineas ?? []).filter(l => l.destino || l.galones > 0).map(l => `${l.destino || 'sin destino'} (${fmtGal(l.galones)} gal)`).join(' · '),
  galones: `${fmtGal((p.lineas ?? []).reduce((s, l) => s + (l.galones || 0), 0))} gal`,
  observaciones: p.observaciones
};

// Ícono con el que se reconoce cada suplidor de un vistazo. Coastal despacha
// por terminal marítima, de ahí el barco.
const ICONO_TIPO: Record<PedidoTipo, React.ElementType> = {
  GLP_REFINERIA: Fuel,
  GLP_COASTAL: Ship,
  WOP: Fuel
};
// Color del distintivo en el listado, para distinguir a qué suplidor fue cada
// orden sin tener que leer la etiqueta.
const COLOR_TIPO: Record<PedidoTipo, {
  color: string;
  soft: string;
}> = {
  GLP_REFINERIA: {
    color: T.brand,
    soft: T.accentSoft
  },
  GLP_COASTAL: {
    color: T.info,
    soft: T.infoSoft
  },
  WOP: {
    color: T.textMuted,
    soft: T.surfaceSubtle
  }
};

// ── EL NÚMERO DE ORDEN NO SE TOCA ───────────────────────────────────────────
// Lo asigna el sistema siguiendo la secuencia de cada suplidor (Refinería y
// Coastal llevan la suya). Un número tecleado a mano se repite o se salta, y
// este va impreso en un documento que sale de la empresa.
//
// Va como campo de solo lectura y no como texto suelto a propósito: con texto
// suelto, quien intenta borrarlo no consigue nada y no entiende por qué. Así
// el cursor entra, la tecla no escribe, y el aviso dice el motivo.
function NumeroOrdenFijo({
  numero,
  editando
}: {
  numero: string;
  editando: boolean;
}) {
  const isMobile = useIsMobile();

  // Se guarda también el número de intento, y no solo el texto: como el aviso
  // dice siempre lo mismo, sin ese contador React no vería ningún cambio de
  // estado, el temporizador no se reiniciaría, y el aviso se apagaría a los
  // 3,2 segundos del PRIMER intento aunque siguieras dándole a la tecla.
  const [aviso, setAviso] = useState<{
    texto: string;
    intento: number;
  } | null>(null);

  // Se borra solo: es una corrección momentánea, no un error pendiente de
  // resolver.
  useEffect(() => {
    if (!aviso) return;
    const t = setTimeout(() => setAviso(null), 3200);
    return () => clearTimeout(t);
  }, [aviso]);
  const negar = () => setAviso(prev => ({
    texto: editando ? 'El número de orden no se puede cambiar: quedó fijado al crear la orden.' : 'El número de orden no se puede escribir ni borrar: lo asigna el sistema.',
    intento: (prev?.intento ?? 0) + 1
  }));

  // Escribir, borrar o pegar avisan. Copiar y moverse con las flechas no:
  // llevarse el número al portapapeles es legítimo.
  const intentaCambiar = (e: React.KeyboardEvent) => e.key === 'Backspace' || e.key === 'Delete' || e.key.length === 1 && !e.ctrlKey && !e.metaKey;
  const leyenda = editando ? 'no se cambia' : 'se asigna solo';
  return <div data-id="element-919" data-mp="uydrue7rodna">
      <div style={{
      position: 'relative'
    }} data-id="element-920" data-mp="uy44yzi0qmlt">
        <input readOnly value={numero || '—'} title="Lo asigna el sistema: no se puede cambiar" aria-label="Orden de pedido número. Lo asigna el sistema y no se puede cambiar." style={{
        ...inputStyle,
        background: aviso ? 'rgba(220,38,38,.05)' : T.surfaceSubtle,
        border: aviso ? '1px solid rgba(220,38,38,.55)' : inputStyle.border,
        color: T.text,
        fontWeight: 800,
        fontSize: 16,
        letterSpacing: '.02em',
        paddingRight: isMobile ? 104 : 128,
        cursor: 'default'
      }} onKeyDown={e => {
        if (intentaCambiar(e)) {
          e.preventDefault();
          negar();
        }
      }} onPaste={e => {
        e.preventDefault();
        negar();
      }} onCut={e => {
        e.preventDefault();
        negar();
      }} onDrop={e => {
        e.preventDefault();
        negar();
      }} data-id="element-921" data-mp="zwp22q1z4q61" />

        <span style={{
        position: 'absolute',
        right: 12,
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        fontSize: 11,
        fontWeight: 600,
        color: aviso ? T.danger : T.textMuted,
        letterSpacing: 0,
        whiteSpace: 'nowrap',
        pointerEvents: 'none'
      }} data-id="element-922" data-mp="3i05cfhfab9z7">

          <Lock size={11} data-id="element-923" data-mp="16bw95fh4twcb" />
          {leyenda}
        </span>
      </div>

      {aviso ? <div style={{
      marginTop: 5,
      fontSize: 11.5,
      fontWeight: 600,
      color: T.danger,
      display: 'flex',
      alignItems: 'center',
      gap: 5
    }} data-id="element-924" data-mp="11e489vyiezvz">

          <span style={{
        width: 14,
        height: 14,
        borderRadius: '50%',
        background: T.dangerSoft,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 10,
        flexShrink: 0
      }} data-id="element-925" data-mp="30lkx9vdcleej">

            !
          </span>
          {aviso.texto}
        </div> : null}
    </div>;
}

// ── TARJETA DE UN SUPLIDOR ──────────────────────────────────────────────────
// Refinería y Coastal son dos contratos aparte: cada uno con su numeración,
// su destinatario y su despacho. El resumen de arriba los suma, y ahí se
// pierde lo único que hace falta para llamar a uno o al otro.
//
// Se pulsa para filtrar el listado por ese suplidor, y se vuelve a pulsar
// para quitar el filtro.
function TarjetaSuplidor({
  datos,
  activa,
  onClick
}: {
  datos: {
    tipo: PedidoTipo;
    total: number;
    borradores: number;
    autorizadas: number;
    galonesMes: number;
    ultimo: number | null;
    siguiente: string;
  };
  activa: boolean;
  onClick: () => void;
}) {
  const isMobile = useIsMobile();
  const color = COLOR_TIPO[datos.tipo];
  const Icono = ICONO_TIPO[datos.tipo];
  const sup = SUPLIDORES[datos.tipo];
  const dato = (etiqueta: string, valor: React.ReactNode, resaltado = false) => <div data-id="element-926" data-mp="17viooitar9xj">
      <div style={{
      fontSize: 10,
      fontWeight: 800,
      letterSpacing: '.09em',
      textTransform: 'uppercase',
      color: T.textMuted,
      whiteSpace: 'nowrap'
    }} data-id="element-927" data-mp="17v8t8fphgakg">

        {etiqueta}
      </div>
      <div style={{
      fontSize: 14,
      fontWeight: 800,
      marginTop: 2,
      color: resaltado ? color.color : T.text,
      fontVariantNumeric: 'tabular-nums'
    }} data-id="element-928" data-mp="1hmmzr7fuqan3">

        {valor}
      </div>
    </div>;
  // El nombre accesible dice "Resumen de…" y no solo la etiqueta del suplidor
  // porque el formulario tiene un botón con ese mismo texto para elegirlo: si
  // los dos se llamaran igual, quien navegue por voz o con lector de pantalla
  // no sabría cuál es cuál.
  return <button type="button" onClick={onClick} aria-label={`Resumen de ${sup.etiqueta}`} title={activa ? 'Quitar el filtro' : `Ver solo las órdenes de ${sup.corta}`} style={{
    ...glassLight,
    textAlign: 'left',
    fontFamily: 'inherit',
    cursor: 'pointer',
    padding: 0,
    overflow: 'hidden',
    border: `1px solid ${activa ? `${color.color}66` : T.border}`,
    boxShadow: activa ? `0 0 0 3px ${color.soft}` : undefined,
    transition: 'border-color .15s, box-shadow .15s'
  }} data-id="element-929" data-mp="3namf5he960k">

      {/* Franja del color del suplidor, igual que en el listado. */}
      <div style={{
      height: 4,
      background: color.color
    }} data-id="element-930" data-mp="wan5v6s9cxpn" />

      <div style={{
      padding: isMobile ? '14px 15px' : '16px 18px'
    }} data-id="element-931" data-mp="wcpvvqteuqud">
        <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        marginBottom: 12
      }} data-id="element-932" data-mp="10ebdfpgw3taw">

          <span style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 28,
          height: 28,
          borderRadius: 9,
          flexShrink: 0,
          background: color.soft,
          color: color.color
        }} data-id="element-933" data-mp="2ptm4zzmpiy6v">

            <Icono size={15} data-id="element-934" data-mp="2gus96bh7abud" />
          </span>
          <div style={{
          minWidth: 0
        }} data-id="element-935" data-mp="2buw6qgo5isgx">
            <div style={{
            fontSize: 13.5,
            fontWeight: 800,
            color: T.text,
            lineHeight: 1.2
          }} data-id="element-936" data-mp="38k3rhmgy4xjs">
              {sup.etiqueta}
            </div>
            <div style={{
            fontSize: 10.5,
            color: T.textMuted,
            marginTop: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }} data-id="element-937" data-mp="2zrtho69yqu32">

              {sup.razonSocial}
            </div>
          </div>
        </div>

        <div style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 9,
        marginBottom: 13
      }} data-id="element-938" data-mp="3nhntckijqclq">

          <span style={{
          fontSize: 30,
          fontWeight: 800,
          lineHeight: 1,
          color: color.color,
          fontVariantNumeric: 'tabular-nums'
        }} data-id="element-939" data-mp="2gt7j4enlp5l2">

            {datos.total}
          </span>
          <span style={{
          fontSize: 11.5,
          color: T.textMuted,
          fontWeight: 600
        }} data-id="element-940" data-mp="2pqzyfitbod8g">
            {datos.total === 1 ? 'orden' : 'órdenes'}
            {datos.borradores > 0 ? ` · ${datos.borradores} sin autorizar` : ''}
          </span>
        </div>

        <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0,1fr))',
        gap: 10,
        paddingTop: 12,
        borderTop: `1px solid ${T.border}`
      }} data-id="element-941" data-mp="2urnyetzj44et">

          {dato('Galones del mes', fmtGal(datos.galonesMes))}
          {dato('Última orden', datos.ultimo === null ? '—' : String(datos.ultimo))}
          {dato('Próxima', datos.siguiente, true)}
        </div>
      </div>
    </button>;
}

// Fecha en yyyy-mm-dd tomada del calendario LOCAL. `toISOString()` convierte a
// UTC, y como República Dominicana va cuatro horas atrás, después de las 8 de
// la noche devolvía el día siguiente: las órdenes de la tarde quedaban
// fechadas mañana.
const isoLocal = (d: Date) => {
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mes}-${dia}`;
};
const hoyISO = () => isoLocal(new Date());

// Atajos del filtro de fecha. La semana arranca el lunes.
type RangoFecha = 'todas' | 'hoy' | 'semana' | 'mes';
const RANGOS: {
  id: RangoFecha;
  label: string;
}[] = [{
  id: 'todas',
  label: 'Todas'
}, {
  id: 'hoy',
  label: 'Hoy'
}, {
  id: 'semana',
  label: 'Esta semana'
}, {
  id: 'mes',
  label: 'Este mes'
}];
const limitesRango = (modo: RangoFecha): {
  desde: string;
  hasta: string;
} | null => {
  if (modo === 'todas') return null;
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  if (modo === 'hoy') {
    return {
      desde: isoLocal(hoy),
      hasta: isoLocal(hoy)
    };
  }
  if (modo === 'semana') {
    const desdeLunes = (hoy.getDay() + 6) % 7; // domingo (0) cuenta como 6
    const lunes = new Date(hoy);
    lunes.setDate(hoy.getDate() - desdeLunes);
    const domingo = new Date(lunes);
    domingo.setDate(lunes.getDate() + 6);
    return {
      desde: isoLocal(lunes),
      hasta: isoLocal(domingo)
    };
  }
  const primero = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
  const ultimo = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
  return {
    desde: isoLocal(primero),
    hasta: isoLocal(ultimo)
  };
};
const totalGalones = (p: Pedido) => p.lineas.reduce((sum, l) => sum + (l.galones || 0), 0);
const estadoStyle = (estado: Pedido['estado']) => {
  if (estado === 'autorizado') return {
    color: T.ok,
    soft: T.okSoft,
    label: 'Autorizado'
  };
  if (estado === 'anulado') return {
    color: T.danger,
    soft: T.dangerSoft,
    label: 'Anulado'
  };
  return {
    color: T.warn,
    soft: T.warnSoft,
    label: 'Borrador'
  };
};
const iconBtn: React.CSSProperties = {
  width: 30,
  height: 30,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 8,
  border: `1px solid ${T.border}`,
  background: T.surface,
  cursor: 'pointer'
};

// Línea vacía para un compartimiento que todavía no se llenó.
const lineaVacia = (compartimiento: number): PedidoLinea => ({
  compartimiento,
  producto: 'gasoilRegular',
  galones: 0,
  destino: '',
  rncDestino: ''
});
const lineaGLPVacia = (): PedidoLinea => ({
  compartimiento: 1,
  producto: 'glp',
  galones: 0,
  destino: '',
  rncDestino: ''
});
const pedidoVacio = (): Partial<Pedido> => ({
  tipo: 'GLP_REFINERIA',
  numeroOrden: '',
  fecha: hoyISO(),
  conductor: '',
  paseChofer: '',
  fichaCola: '',
  placaCamion: '',
  lineas: [lineaGLPVacia()],
  estado: 'borrador',
  observaciones: ''
});
interface PedidosProps {
  pedidos: Pedido[];
  onAdd: (p: Pedido) => void;
  onUpdate: (p: Pedido) => void;
  onArchivar: (pedido: Pedido, archivar: boolean) => void;
  // Catálogos de "Flota y Destinos". Alimentan los desplegables del
  // formulario: elegir un conductor trae su pase, su placa, su cola y sus
  // galones; elegir una envasadora trae su RNC. Se mantienen en su propio
  // módulo, aquí solo se consultan.
  conductores: Conductor[];
  envasadoras: Envasadora[];
  camiones: Camion[];
  colas: Cola[];
  // Si el usuario no tiene permiso de ver Flota, no tiene sentido mandarlo
  // allá cuando las listas están vacías.
  puedeVerFlota: boolean;
  // Permisos resueltos por el rol del usuario (ver App.tsx).
  canEdit: boolean;
  canArchive: boolean;
  canAutorizar: boolean;
  // Quién firma cuando se autoriza una orden.
  usuarioNombre: string;
  usuarioCargo: string;
  onLog?: (mensaje: string, detalle?: DetalleAuditoria) => void;
}
export function Pedidos({
  pedidos,
  onAdd,
  onUpdate,
  onArchivar,
  // Los catálogos llevan valor por defecto a propósito: si por lo que sea
  // llegan sin definir (App.tsx sin actualizar, caché a medias), el módulo
  // sigue en pie y se comporta como si no hubiera nada registrado, en vez de
  // dejar la pantalla en blanco.
  conductores = [],
  envasadoras = [],
  camiones = [],
  colas = [],
  puedeVerFlota = false,
  canEdit,
  canArchive,
  canAutorizar,
  usuarioNombre,
  usuarioCargo,
  onLog
}: PedidosProps) {
  const isMobile = useIsMobile();
  const [dialogo, confirmar] = useConfirmacion();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTipo, setFilterTipo] = useState<'todos' | PedidoTipo>('todos');
  const [filterEstado, setFilterEstado] = useState<'todos' | 'borrador' | 'autorizado' | 'anulado'>('todos');
  const [filterFecha, setFilterFecha] = useState<RangoFecha>('todas');
  const [verArchivados, setVerArchivados] = useState(false);
  const [form, setForm] = useState<Partial<Pedido>>(pedidoVacio());
  const lineas = form.lineas ?? [];
  // Igual que en Flota: editar una orden del final del listado abre el
  // formulario arriba, así que hay que llevar la vista hasta él.
  const refForm = useDesplazarAlAbrir<HTMLFormElement>(showForm, editingId);

  // ── FILTRADO ──────────────────────────────────────────────────────────
  // El listado muestra las órdenes en curso o las archivadas, nunca mezcladas.
  // Todo lo demás (buscador, filtros, contadores) trabaja sobre este conjunto.
  const enVista = useMemo(() => pedidos.filter(p => verArchivados ? !!p.archivado : !p.archivado), [pedidos, verArchivados]);
  const totalArchivadas = useMemo(() => pedidos.filter(p => !!p.archivado).length, [pedidos]);

  // Las fechas son yyyy-mm-dd, así que comparar con < y > alfabéticamente da
  // el mismo resultado que comparar como fechas y evita construir Dates.
  const filtrados = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const rango = limitesRango(filterFecha);
    return enVista.filter(p => filterTipo === 'todos' || normalizarTipo(p.tipo) === filterTipo).filter(p => filterEstado === 'todos' || p.estado === filterEstado).filter(p => !rango || !!p.fecha && p.fecha >= rango.desde && p.fecha <= rango.hasta).filter(p => {
      if (!q) return true;
      const enDestinos = p.lineas.some(l => l.destino.toLowerCase().includes(q) || l.rncDestino.toLowerCase().includes(q));
      return p.numeroOrden.toLowerCase().includes(q) || p.conductor.toLowerCase().includes(q) || p.placaCamion.toLowerCase().includes(q) || enDestinos;
    }).sort((a, b) => (b.fecha || '').localeCompare(a.fecha || ''));
  }, [enVista, searchTerm, filterTipo, filterEstado, filterFecha]);

  // Cuántas órdenes caen en cada atajo de fecha: saberlo antes de hacer clic
  // ahorra el viaje de ir y volver cuando el rango está vacío.
  const conteoRango = useMemo(() => {
    const cuenta = {} as Record<RangoFecha, number>;
    for (const r of RANGOS) {
      const lim = limitesRango(r.id);
      cuenta[r.id] = lim ? enVista.filter(p => !!p.fecha && p.fecha >= lim.desde && p.fecha <= lim.hasta).length : enVista.length;
    }
    return cuenta;
  }, [enVista]);
  const {
    page,
    setPage,
    totalPages,
    pageItems,
    rangeStart,
    rangeEnd,
    total
  } = usePagination(filtrados);

  // Al devolver la última orden archivada no tiene sentido quedarse mirando una
  // sección vacía: se vuelve solo al listado, que es justo donde acaba de
  // reaparecer la orden.
  useEffect(() => {
    if (verArchivados && totalArchivadas === 0) {
      setVerArchivados(false);
      setPage(1);
    }
  }, [verArchivados, totalArchivadas]);

  // ── ESTADÍSTICAS ──────────────────────────────────────────────────────
  // Los recuadros de arriba cuentan solo las órdenes en curso: una archivada
  // ya no está en el listado, así que sumarla haría que los números no
  // cuadraran con lo que se ve debajo.
  const stats = useMemo(() => {
    const activas = pedidos.filter(p => !p.archivado);
    const vivos = activas.filter(p => p.estado !== 'anulado');
    const mes = hoyISO().slice(0, 7);
    return {
      total: activas.length,
      borradores: activas.filter(p => p.estado === 'borrador').length,
      autorizados: activas.filter(p => p.estado === 'autorizado').length,
      galonesMes: vivos.filter(p => (p.fecha || '').startsWith(mes)).reduce((s, p) => s + totalGalones(p), 0)
    };
  }, [pedidos]);

  // ── LO DE CADA SUPLIDOR POR SEPARADO ──────────────────────────────────
  // Refinería y Coastal son dos contratos distintos, con su propia
  // numeración y su propio despacho: sumarlos en un solo total esconde
  // justo el dato que hace falta para llamar a uno o al otro.
  //
  // El último número usado sale de TODAS las órdenes, incluidas las
  // archivadas y las anuladas: ese número ya se gastó aunque la orden se
  // haya dado de baja. Los contadores, en cambio, solo miran las activas,
  // que es lo que se está manejando hoy.
  const porSuplidor = useMemo(() => {
    const mes = hoyISO().slice(0, 7);
    const tipos: PedidoTipo[] = [...TIPOS_DISPONIBLES, 'WOP'];
    return tipos.map(t => {
      const suyas = pedidos.filter(p => normalizarTipo(p.tipo) === t);
      const activas = suyas.filter(p => !p.archivado);
      const vivas = activas.filter(p => p.estado !== 'anulado');
      const numeros = suyas.map(p => parseInt(String(p.numeroOrden ?? '').replace(/\D/g, ''), 10)).filter(n => Number.isFinite(n));
      return {
        tipo: t,
        total: activas.length,
        borradores: activas.filter(p => p.estado === 'borrador').length,
        autorizadas: activas.filter(p => p.estado === 'autorizado').length,
        galonesMes: vivas.filter(p => (p.fecha || '').startsWith(mes)).reduce((s, p) => s + totalGalones(p), 0),
        ultimo: numeros.length ? Math.max(...numeros) : null,
        siguiente: siguienteNumeroOrden(pedidos, t)
      };
      // WOP solo aparece si quedan órdenes viejas de ese formato: ya no se
      // crean, y una tarjeta en cero todos los días solo estorba.
    }).filter(s => s.tipo !== 'WOP' || s.total > 0);
  }, [pedidos]);

  // WOP ya no se ofrece al crear, pero si quedan órdenes viejas del formato
  // hace falta poder filtrarlas. El filtro aparece solo si existen.
  const hayWOP = useMemo(() => pedidos.some(p => esWOP(p.tipo)), [pedidos]);

  // ── FORMULARIO ────────────────────────────────────────────────────────
  const abrirNuevo = () => {
    const vacio = pedidoVacio();
    setForm({
      ...vacio,
      numeroOrden: siguienteNumeroOrden(pedidos, normalizarTipo(vacio.tipo ?? ''))
    });
    setEditingId(null);
    setShowForm(true);
  };
  const abrirEdicion = (p: Pedido) => {
    // Una orden ya creada conserva su número pase lo que pase: es el que el
    // suplidor tiene registrado y el que va impreso en el papel.
    setForm({
      ...p,
      lineas: p.lineas.map(l => ({
        ...l
      }))
    });
    setEditingId(p.id);
    setShowForm(true);
  };
  const cerrarForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(pedidoVacio());
  };

  // Cambiar de suplidor no toca el detalle: los dos formatos de GLP son el
  // mismo papel. Solo hay que rehacer las líneas si se viene de una orden
  // vieja de WOP, porque sus 6 compartimientos con producto no son
  // intercambiables con la línea única de GLP.
  const cambiarTipo = (tipo: PedidoTipo) => {
    setForm(prev => {
      const veniaDeWOP = esWOP(prev.tipo ?? '');
      const vaAWOP = esWOP(tipo);
      // Cada suplidor lleva su propia secuencia, así que al cambiar de uno a
      // otro el número tiene que cambiar también. Solo en órdenes nuevas y si
      // nadie lo ha escrito a mano.
      // Una orden ya creada conserva su número: es el que el suplidor tiene
      // registrado y el que va impreso en el papel.
      const numeroOrden = editingId ? prev.numeroOrden : siguienteNumeroOrden(pedidos, tipo);
      if (veniaDeWOP === vaAWOP) return {
        ...prev,
        tipo,
        numeroOrden
      };
      return {
        ...prev,
        tipo,
        numeroOrden,
        lineas: vaAWOP ? COMPARTIMIENTOS.map(lineaVacia) : [lineaGLPVacia()]
      };
    });
  };
  // ── CATÁLOGOS Y REGLAS ────────────────────────────────────────────────
  const conductoresActivos = useMemo(() => (conductores ?? []).filter(c => c.activo !== false).sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')), [conductores]);
  const envasadorasActivas = useMemo(() => (envasadoras ?? []).filter(e => e.activo !== false).sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')), [envasadoras]);
  const camionesActivos = useMemo(() => (camiones ?? []).filter(c => c.activo !== false), [camiones]);
  const colasActivas = useMemo(() => (colas ?? []).filter(c => c.activo !== false), [colas]);

  // El chofer de una orden vieja puede no estar registrado (o haberse dado de
  // baja). Se le agrega su propia opción para que el desplegable no lo pierda
  // al editar.
  const conductorFuera = !!form.conductor?.trim() && !conductoresActivos.some(c => c.nombre === form.conductor);
  const conductorActual = useMemo(() => conductoresActivos.find(c => c.nombre === form.conductor), [conductoresActivos, form.conductor]);
  // El destino manda: de él dependen qué colas y qué camiones se pueden usar.
  const destinoActual = useMemo(() => envasadorasActivas.find(e => e.nombre === (form.lineas?.[0]?.destino ?? '')), [envasadorasActivas, form.lineas]);

  // Un conductor sin unidades asignadas puede llevar cualquiera de las que
  // estén en servicio; con unidades asignadas, solo las suyas.
  const camionesDelConductor = useMemo(() => {
    const suyosLista = conductorActual?.camiones ?? [];
    if (!suyosLista.length) return camionesActivos;
    const suyos = new Set(suyosLista.map(k => k.id));
    return camionesActivos.filter(c => suyos.has(c.id));
  }, [conductorActual, camionesActivos]);
  const colasDelConductor = useMemo(() => {
    const suyasLista = conductorActual?.colas ?? [];
    if (!suyasLista.length) return colasActivas;
    const suyas = new Set(suyasLista.map(k => k.id));
    return colasActivas.filter(c => suyas.has(c.id));
  }, [conductorActual, colasActivas]);

  // Camiones que pueden hacer ESTE viaje: ni vetados en la planta de destino,
  // ni reservados a otro chofer.
  const camionesValidos = useMemo(() => camionesDelConductor.filter(c => (!destinoActual || !(c.plantasVetadas ?? []).includes(destinoActual.id)) && (!c.soloConductorId || c.soloConductorId === conductorActual?.id)), [camionesDelConductor, destinoActual, conductorActual]);

  // Colas admitidas por la planta, separadas en primera y segunda opción.
  // Sin destino elegido todavía, se ofrecen todas las del conductor.
  const {
    colasPrimera,
    colasSegunda
  } = useMemo(() => {
    if (!destinoActual) return {
      colasPrimera: colasDelConductor,
      colasSegunda: [] as typeof colasDelConductor
    };
    const segundas = destinoActual.colasSegundaOpcion ?? [];
    const permitidas = destinoActual.colasPermitidas ?? [];
    return {
      colasPrimera: colasDelConductor.filter(c => permitidas.length ? permitidas.includes(c.id) : !segundas.includes(c.id)),
      colasSegunda: colasDelConductor.filter(c => segundas.includes(c.id))
    };
  }, [destinoActual, colasDelConductor]);
  const colaPorFicha = (ficha: string) => colasActivas.find(c => c.ficha === ficha);
  const camionPorPlaca = (placa: string) => camionesActivos.find(c => c.placa === placa);

  // Qué tiene de malo la combinación elegida. Se calcula en cada render para
  // poder avisar mientras se arma la orden, no solo al intentar guardarla.
  const problemas: string[] = [];
  if (destinoActual) {
    const camion = camionPorPlaca(form.placaCamion ?? '');
    if (camion && (camion.plantasVetadas ?? []).includes(destinoActual.id)) {
      problemas.push(`${camion.nombre} (${camion.placa}) no puede entrar a ${destinoActual.nombre}.`);
    }
    if (camion?.soloConductorId && camion.soloConductorId !== conductorActual?.id) {
      problemas.push(`${camion.nombre} solo lo puede llevar ${camion.soloConductorNombre}.`);
    }
    const cola = colaPorFicha(form.fichaCola ?? '');
    const permitidas = destinoActual.colasPermitidas ?? [];
    const segundas = destinoActual.colasSegundaOpcion ?? [];
    if (cola && permitidas.length && !permitidas.includes(cola.id) && !segundas.includes(cola.id)) {
      problemas.push(`${destinoActual.nombre} no admite la cola ${cola.ficha}.`);
    }
  }

  // Elegir el conductor arrastra su unidad: pase, placa y ficha de la cola.
  // Es el punto de todo el registro de flota: media orden se llena de un solo
  // clic, y sin teclear no hay erratas en un papel que sale de la empresa.
  const elegirConductor = (nombre: string) => {
    const c = conductoresActivos.find(x => x.nombre === nombre);
    setForm(prev => ({
      ...prev,
      conductor: nombre,
      paseChofer: c?.pase || prev.paseChofer,
      placaCamion: c?.camiones?.[0]?.placa || prev.placaCamion,
      fichaCola: c?.colas?.[0]?.ficha || prev.fichaCola
    }));
  };

  // Elegir la cola decide los galones del viaje: son suyos, no del chofer ni
  // del camión. La misma cola carga siempre lo mismo.
  const elegirCola = (ficha: string) => {
    const cola = colaPorFicha(ficha);
    setForm(prev => ({
      ...prev,
      fichaCola: ficha,
      lineas: cola?.capacidadGalones ? (prev.lineas ?? []).map((l, i) => i === 0 ? {
        ...l,
        galones: cola.capacidadGalones
      } : l) : prev.lineas
    }));
  };

  // Elegir el destino trae su RNC, que es el otro dato que se imprime.
  const elegirEnvasadora = (index: number, nombre: string) => {
    const e = envasadorasActivas.find(x => x.nombre === nombre);
    setLinea(index, {
      destino: nombre,
      rncDestino: e?.rnc ?? ''
    });
  };
  const setLinea = (index: number, patch: Partial<PedidoLinea>) => {
    setForm(prev => ({
      ...prev,
      lineas: (prev.lineas ?? []).map((l, i) => i === index ? {
        ...l,
        ...patch
      } : l)
    }));
  };
  // Qué falta para poder guardar. Se calcula en cada render para poder
  // señalar el campo concreto en vez de soltar un alert con una sola queja
  // a la vez.
  const faltantes: Record<string, string> = {};
  if (!form.fecha) faltantes.fecha = 'Indica la fecha del pedido';
  if (!form.conductor?.trim()) faltantes.conductor = 'Indica el conductor';
  if (!form.placaCamion?.trim()) faltantes.placaCamion = 'Indica la placa del camión';
  const lineasUtiles = (form.lineas ?? []).filter(l => (l.galones || 0) > 0);
  if (lineasUtiles.length === 0) {
    faltantes.lineas = esWOP(form.tipo ?? '') ? 'Registra al menos un compartimiento con galones' : 'Indica la cantidad de galones';
  } else if (lineasUtiles.some(l => !l.destino.trim())) {
    faltantes.lineas = 'Cada línea con galones necesita su destino';
  }
  const [mostrarFaltantes, setMostrarFaltantes] = useState(false);
  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    // Las reglas de flota se comprueban aquí también, no solo en pantalla: el
    // formulario se puede haber armado en otro orden y el aviso quedar fuera
    // de vista. Un documento con una combinación imposible no debe salir.
    if (problemas.length) {
      setMostrarFaltantes(true);
      return;
    }
    // Los errores solo se destapan al intentar guardar: marcar campos en rojo
    // apenas se abre el formulario es hostil.
    if (Object.keys(faltantes).length) {
      setMostrarFaltantes(true);
      return;
    }
    // El número se vuelve a calcular justo antes de guardar, no se usa el que
    // se puso al abrir el formulario. Con el tiempo real, otro usuario puede
    // haber creado una orden mientras este formulario estaba abierto: ese
    // número ya estaría ocupado y la base lo rechazaría por el índice único.
    // Como el campo ya no se puede corregir a mano, esto tiene que salir bien
    // solo.
    const base = {
      ...form,
      numeroOrden: editingId ? form.numeroOrden : siguienteNumeroOrden(pedidos, normalizarTipo(form.tipo ?? '')),
      lineas: lineasUtiles
    } as Pedido;
    const galones = lineasUtiles.reduce((sum, l) => sum + (l.galones || 0), 0);
    const ok = await confirmar({
      titulo: editingId ? '¿Guardar los cambios?' : '¿Crear esta orden de pedido?',
      mensaje: editingId ? 'La orden se actualizará con los datos del formulario.' : 'La orden quedará en borrador hasta que alguien la autorice.',
      detalles: [{
        etiqueta: 'Orden',
        valor: base.numeroOrden
      }, {
        etiqueta: 'Formato',
        valor: etiquetaTipo(base.tipo)
      }, {
        etiqueta: 'Conductor',
        valor: base.conductor
      }, {
        etiqueta: 'Placa',
        valor: base.placaCamion
      }, {
        etiqueta: lineasUtiles.length === 1 ? 'Destino' : 'Destinos',
        valor: String(lineasUtiles.length)
      }, {
        etiqueta: 'Total',
        valor: `${fmtGal(galones)} galones`
      }],
      confirmar: editingId ? 'Guardar cambios' : 'Crear orden',
      tono: 'accent'
    });
    if (!ok) return;
    if (editingId) {
      onUpdate({
        ...base,
        id: editingId
      });
      const previa = pedidos.find(x => x.id === editingId);
      onLog?.(`Orden de pedido editada: ${base.numeroOrden} (${etiquetaTipo(base.tipo)})`, {
        accion: 'editar',
        entidad: 'Orden de pedido',
        registro: base.numeroOrden,
        cambios: diferencias(vistaPedido(previa), vistaPedido(base as Pedido)!, CAMPOS_PEDIDO)
      });
    } else {
      onAdd({
        ...base,
        id: Date.now().toString(),
        estado: 'borrador',
        autorizadoPor: '',
        autorizadoCargo: '',
        autorizadoFecha: '',
        // El token se acuña al autorizar, no al crear: un borrador todavía
        // no es un documento verificable.
        token: '',
        fecha_creacion: hoyISO()
      });
      onLog?.(`Orden de pedido creada: ${base.numeroOrden} (${etiquetaTipo(base.tipo)})`, {
        accion: 'crear',
        entidad: 'Orden de pedido',
        registro: base.numeroOrden,
        cambios: diferencias(null, vistaPedido(base as Pedido)!, CAMPOS_PEDIDO)
      });
      avisarPorCorreo(base as Pedido, 'creada');
    }
    setMostrarFaltantes(false);
    cerrarForm();
  };

  // ── AVISO POR CORREO ──────────────────────────────────────────────────
  // Sale al crear la orden y al autorizarla. Va sin `await` a propósito: el
  // correo es un aviso, la orden es el documento. Si el envío tarda o falla,
  // la orden ya quedó guardada y el formulario ya se cerró.
  //
  // El resultado se apunta en la auditoría, con su motivo si falló. Así, el
  // día que el suplidor diga que no le llegó nada, la respuesta está en el
  // registro y no en la memoria de alguien.
  const avisarPorCorreo = (p: Pedido, evento: EventoCorreo) => {
    // Sin configurar no es un fallo, es que está apagado: no se registra nada
    // para no llenar la auditoría de ruido.
    if (!correoConfigurado()) return;
    void enviarAvisoPedido(p, evento, usuarioNombre).then(r => {
      const que = evento === 'creada' ? 'creada' : 'autorizada';
      if (r.enviado) {
        onLog?.(`Correo enviado: aviso de orden ${que} ${p.numeroOrden}`, {
          accion: 'correo',
          entidad: 'Orden de pedido',
          registro: p.numeroOrden,
          cambios: [{
            campo: 'Enviado a',
            antes: '',
            despues: r.destinatarios ?? ''
          }]
        });
      } else {
        onLog?.(`Correo no se pudo enviar: aviso de orden ${que} ${p.numeroOrden}`, {
          accion: 'correo',
          entidad: 'Orden de pedido',
          registro: p.numeroOrden,
          cambios: [{
            campo: 'Motivo',
            antes: '',
            despues: r.motivo ?? 'desconocido'
          }]
        });
      }
    });
  };

  // ── AUTORIZACIÓN ──────────────────────────────────────────────────────
  // Autorizar equivale a firmar el formulario: se estampa quién lo hizo y
  // desde ahí el pedido ya no se edita.
  const autorizar = async (p: Pedido) => {
    const ok = await confirmar({
      titulo: '¿Autorizar esta orden de pedido?',
      mensaje: `Tu nombre quedará estampado en el "AUTORIZADO POR" del formulario y se generará su código QR de verificación.`,
      detalles: [{
        etiqueta: 'Orden',
        valor: p.numeroOrden
      }, {
        etiqueta: 'Formato',
        valor: etiquetaTipo(p.tipo)
      }, {
        etiqueta: 'Conductor',
        valor: p.conductor || '—'
      }, {
        etiqueta: 'Total',
        valor: `${fmtGal(totalGalones(p))} galones`
      }, {
        etiqueta: 'Autoriza',
        valor: usuarioNombre
      }],
      advertencia: 'Una vez autorizada, la orden ya no se puede editar.',
      confirmar: 'Sí, autorizar',
      tono: 'ok'
    });
    if (!ok) return;
    const autorizada: Pedido = {
      ...p,
      estado: 'autorizado',
      autorizadoPor: usuarioNombre,
      autorizadoCargo: usuarioCargo,
      autorizadoFecha: hoyISO(),
      // Se acuña una sola vez: si la orden ya se autorizó antes (y se
      // reautoriza tras una corrección) el QR impreso sigue siendo válido.
      token: p.token || nuevoToken()
    };
    onUpdate(autorizada);
    onLog?.(`Orden de pedido autorizada: ${p.numeroOrden} por ${usuarioNombre}`, {
      accion: 'autorizar',
      entidad: 'Orden de pedido',
      registro: p.numeroOrden,
      cambios: [{
        campo: 'Estado',
        antes: estadoStyle(p.estado).label,
        despues: 'Autorizada'
      }, {
        campo: 'Autorizado por',
        antes: p.autorizadoPor || '',
        despues: usuarioNombre
      }]
    });
    // Se manda la orden ya autorizada, no `p`: es la que trae el token, y sin
    // token el correo saldría sin el enlace a la copia digital.
    avisarPorCorreo(autorizada, 'autorizada');
  };
  const anular = async (p: Pedido) => {
    const ok = await confirmar({
      titulo: '¿Anular esta orden?',
      mensaje: 'La orden queda registrada pero marcada como anulada. El formulario impreso saldrá con la marca "ANULADO" y quien escanee su QR verá que ya no es válida.',
      detalles: [{
        etiqueta: 'Orden',
        valor: p.numeroOrden
      }, {
        etiqueta: 'Total',
        valor: `${fmtGal(totalGalones(p))} galones`
      }],
      confirmar: 'Sí, anular',
      tono: 'warn'
    });
    if (!ok) return;
    onUpdate({
      ...p,
      estado: 'anulado'
    });
    onLog?.(`Orden de pedido anulada: ${p.numeroOrden}`, {
      accion: 'anular',
      entidad: 'Orden de pedido',
      registro: p.numeroOrden,
      cambios: [{
        campo: 'Estado',
        antes: estadoStyle(p.estado).label,
        despues: 'Anulada'
      }]
    });
  };
  // Las órdenes no se borran. Archivar las saca del listado y nada más: la
  // orden sigue guardada, se puede volver a ver y el QR de una autorizada se
  // sigue verificando. Para invalidar una orden está Anular, que es otra cosa.
  const archivar = async (p: Pedido, archivando: boolean) => {
    const ok = await confirmar({
      titulo: archivando ? '¿Archivar esta orden?' : '¿Devolver esta orden al listado?',
      mensaje: archivando ? 'Sale del listado principal y queda en "Ver archivados". No se borra nada: la orden se conserva y se puede devolver cuando quieras.' : 'Vuelve al listado principal de órdenes.',
      detalles: [{
        etiqueta: 'Orden',
        valor: p.numeroOrden
      }, {
        etiqueta: 'Estado',
        valor: estadoStyle(p.estado).label
      }, {
        etiqueta: 'Total',
        valor: `${fmtGal(totalGalones(p))} galones`
      }],
      confirmar: archivando ? 'Sí, archivar' : 'Sí, devolver',
      tono: archivando ? 'warn' : 'accent'
    });
    if (!ok) return;
    onArchivar(p, archivando);
    onLog?.(`Orden de pedido ${archivando ? 'archivada' : 'devuelta al listado'}: ${p.numeroOrden}`, {
      accion: archivando ? 'archivar' : 'desarchivar',
      entidad: 'Orden de pedido',
      registro: p.numeroOrden,
      cambios: [{
        campo: 'Archivada',
        antes: archivando ? 'No' : 'Sí',
        despues: archivando ? 'Sí' : 'No'
      }]
    });
  };

  // ── IMPRESIÓN ─────────────────────────────────────────────────────────
  const imprimir = (p: Pedido) => {
    imprimirPedido(p);
    onLog?.(`Orden de pedido impresa: ${p.numeroOrden}`, {
      accion: 'imprimir',
      entidad: 'Orden de pedido',
      registro: p.numeroOrden,
      cambios: [{
        campo: 'Estado al imprimir',
        antes: '',
        despues: estadoStyle(p.estado).label
      }]
    });
  };

  // ── RENDER ────────────────────────────────────────────────────────────
  return <><PageShell title="Órdenes de Pedido" sub="Formulario de orden de pedido / retiro bajo contrato" action={canEdit ? <button onClick={abrirNuevo} style={primaryButton} data-id="element-943" data-mp="31qny31hwrerk">
            <Plus size={15} data-id="element-944" data-mp="31tafrtyijani" /> Nueva orden
          </button> : undefined} data-id="element-942" data-mp="2wsmgkl04rni7">

      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, minmax(0,1fr))',
        gap: 14,
        marginBottom: 26
      }} data-id="element-945" data-mp="3dgtn9xkgb8ds">

        <StatCard label="Órdenes" value={String(stats.total)} note="registradas" data-id="element-946" data-mp="3ngkm2bg1o9jo" />
        <StatCard label="Borradores" value={String(stats.borradores)} note="sin autorizar" accent={T.warn} data-id="element-947" data-mp="3sevv4jyimg0u" />

        <StatCard label="Autorizadas" value={String(stats.autorizados)} note="listas para despacho" accent={T.ok} data-id="element-948" data-mp="2urwuxz8m6py7" />

        <StatCard label="Galones del mes" value={fmtGal(stats.galonesMes)} note="AG solicitados" accent={T.info} data-id="element-949" data-mp="2zqq6vn8968w9" />

      </div>

      {/* ── CADA SUPLIDOR POR SEPARADO ── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : `repeat(${porSuplidor.length}, minmax(0,1fr))`,
        gap: 14,
        marginBottom: 26
      }} data-id="element-950" data-mp="39g00j7iqvzmh">

        {porSuplidor.map(s => <TarjetaSuplidor key={s.tipo} datos={s} activa={filterTipo === s.tipo} onClick={() => {
          setFilterTipo(prev => prev === s.tipo ? 'todos' : s.tipo);
          setPage(1);
        }} data-id="element-951" data-mp="2gy5vjjolkmg7" />)}
      </div>

      {/* ── FILTROS ── */}
      <div style={{
        display: 'flex',
        gap: 10,
        flexWrap: 'wrap',
        marginBottom: 20
      }} data-id="element-952" data-mp="u0woh3af8e7h">

        <div style={{
          position: 'relative',
          flex: '1 1 220px'
        }} data-id="element-953" data-mp="k52zuljfgu9c">

          <Search size={15} style={{
            position: 'absolute',
            left: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            color: T.textMuted
          }} data-id="element-954" data-mp="f58d8u6oqqu3" />

          <input style={{
            ...inputStyle,
            paddingLeft: 34
          }} placeholder="Buscar por orden, conductor, placa o destino" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-955" data-mp="6cn5sa8i86kk" />

        </div>
        <select style={{
          ...inputStyle,
          flex: '0 1 190px'
        }} value={filterTipo} onChange={e => setFilterTipo(e.target.value as typeof filterTipo)} data-id="element-956" data-mp="3sjv6gz8hbpa9">

          <option value="todos" data-id="element-957" data-mp="3dwmpfhshopqd">Todos los formatos</option>
          {TIPOS_DISPONIBLES.map(t => <option key={t} value={t} data-id="element-958" data-mp="3ixagu4kmr8l4">
              {SUPLIDORES[t].etiqueta}
            </option>)}
          {hayWOP && <option value="WOP" data-id="element-959" data-mp="34y1adwm87suc">{SUPLIDORES.WOP.etiqueta}</option>}
        </select>
        <select style={{
          ...inputStyle,
          flex: '0 1 160px'
        }} value={filterEstado} onChange={e => setFilterEstado(e.target.value as typeof filterEstado)} data-id="element-960" data-mp="38y1mvbprltyr">

          <option value="todos" data-id="element-961" data-mp="5hutpz4z7f48">Todos los estados</option>
          <option value="borrador" data-id="element-962" data-mp="5hctkrf70gr1">Borrador</option>
          <option value="autorizado" data-id="element-963" data-mp="3nw3vw1fkf7ne">Autorizado</option>
          <option value="anulado" data-id="element-964" data-mp="3nvlqae70jh2t">Anulado</option>
        </select>
        {/* Las órdenes archivadas no desaparecen: se ven desde aquí y se
             pueden devolver al listado. El botón aparece cuando hay alguna, pero
             también mientras se está viendo la sección: si solo dependiera del
             contador, al devolver la última se esfumaría y no quedaría forma de
             salir de una pantalla vacía. */}
        {(totalArchivadas > 0 || verArchivados) && <button type="button" onClick={() => {
          setVerArchivados(v => !v);
          setPage(1);
          cerrarForm();
        }} style={{
          padding: '9px 15px',
          borderRadius: 99,
          fontSize: 12.5,
          fontWeight: 700,
          cursor: 'pointer',
          fontFamily: 'inherit',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 7,
          whiteSpace: 'nowrap',
          color: verArchivados ? T.warn : T.textSecondary,
          background: verArchivados ? T.warnSoft : 'transparent',
          border: `1px solid ${verArchivados ? `${T.warn}44` : T.border}`,
          transition: 'all .15s'
        }} data-id="element-965" data-mp="23ymvjb1irrgz">

            <Archive size={14} data-id="element-966" data-mp="3bcgj53gekn6n" />
            {verArchivados ? 'Viendo archivadas' : 'Ver archivadas'}
            <span style={{
            fontSize: 11,
            fontWeight: 800,
            padding: '1px 7px',
            borderRadius: 99,
            background: verArchivados ? 'rgba(255,255,255,.7)' : T.surfaceSubtle,
            color: verArchivados ? T.warn : T.textMuted
          }} data-id="element-967" data-mp="36d3ohwjaibti">

              {totalArchivadas}
            </span>
          </button>}
      </div>

      {/* ── FILTRO POR FECHA ──
               Va en botones y no en otro desplegable porque son los cuatro cortes
               que se piden todos los días: un clic en vez de abrir y elegir. */}
      <div style={{
        display: 'flex',
        gap: 7,
        flexWrap: 'wrap',
        marginBottom: 20
      }} data-id="element-968" data-mp="23uqjz13klb5y">

        {RANGOS.map(r => {
          const activo = filterFecha === r.id;
          return <button key={r.id} type="button" onClick={() => setFilterFecha(r.id)} style={{
            padding: '9px 15px',
            borderRadius: 99,
            fontSize: 12.5,
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: 'inherit',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
            whiteSpace: 'nowrap',
            color: activo ? T.accent : T.textSecondary,
            background: activo ? T.accentSoft : 'transparent',
            border: `1px solid ${activo ? T.accentBorder : T.border}`,
            transition: 'all .15s'
          }} data-id="element-969" data-mp="2cntvv4ykhfyh">

              {r.label}
              <span style={{
              fontSize: 11,
              fontWeight: 800,
              padding: '1px 7px',
              borderRadius: 99,
              background: activo ? 'rgba(255,255,255,.7)' : T.surfaceSubtle,
              color: activo ? T.accent : T.textMuted
            }} data-id="element-970" data-mp="2is05ckkce4mw">

                {conteoRango[r.id]}
              </span>
            </button>;
        })}
      </div>

      {/* ── FORMULARIO ── */}
      <AnimatePresence data-id="element-971" data-mp="21jndl9z6wtz">
        {showForm && <motion.form ref={refForm} onSubmit={guardar} initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} style={{
          ...glassLight,
          padding: isMobile ? 18 : 24,
          marginBottom: 24,
          overflow: 'hidden'
        }} data-id="element-972" data-mp="22ky1on2xhb6">

            <SectionTitle data-id="element-973" data-mp="1hrkffuimdgrt">
              {editingId ? 'Editar orden de pedido' : 'Nueva orden de pedido'}
            </SectionTitle>

            <Field label="Suplidor" data-id="element-974" data-mp="1mp2hd0y7yvqb">
              <div style={{
              display: 'flex',
              gap: 10,
              flexWrap: 'wrap'
            }} data-id="element-975" data-mp="18re86qdild13">

                {/* WOP solo se ofrece si esta orden ya venía en ese formato:
                 las nuevas son siempre GLP, pero editar una vieja no debe
                 cambiarle el formato a la fuerza. */}
                {(esWOP(form.tipo ?? '') ? [...TIPOS_DISPONIBLES, 'WOP' as PedidoTipo] : TIPOS_DISPONIBLES).map(t => {
                const activo = normalizarTipo(form.tipo ?? '') === t;
                const Icono = ICONO_TIPO[t];
                return <button key={t} type="button" onClick={() => cambiarTipo(t)} style={{
                  ...(activo ? primaryButton : ghostButton),
                  flex: '1 1 180px',
                  justifyContent: 'center'
                }} data-id="element-976" data-mp="miz1xftn807b">

                      <Icono size={15} data-id="element-977" data-mp="wammcwrs8gza" />
                      {SUPLIDORES[t].etiqueta}
                    </button>;
              })}
              </div>
              <div style={{
              marginTop: 8,
              fontSize: 11.5,
              color: T.textMuted
            }} data-id="element-978" data-mp="159giyn5457cu">

                El formulario sale dirigido a{' '}
                <strong style={{
                color: T.textSecondary
              }} data-id="element-979" data-mp="1f7w5ect73sg0">

                  {SUPLIDORES[normalizarTipo(form.tipo ?? '')].razonSocial}
                </strong>
              </div>
            </Field>

            <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: 16
          }} data-id="element-980" data-mp="3gyhi810rykqj">

              <Field label="Orden de pedido número" data-id="element-981" data-mp="3c2iy4pmhbovx">
                <NumeroOrdenFijo numero={form.numeroOrden ?? ''} editando={!!editingId} data-id="element-982" data-mp="2q5z2sih7afdf" />
              </Field>
              <Field label="Fecha *" data-id="element-983" data-mp="2v4ismtyx0bb2">
                <input type="date" style={inputStyle} value={form.fecha ?? ''} onChange={e => setForm({
                ...form,
                fecha: e.target.value
              })} data-id="element-984" data-mp="2zwtsxxxkhz45" />

              </Field>
              {/* El destino manda: de él dependen qué cola y qué camión se
               pueden usar, así que se elige antes que nada. */}
              {!esWOP(form.tipo ?? '') && <div style={{
              gridColumn: isMobile ? 'auto' : '1 / -1'
            }} data-id="element-985" data-mp="2c9bw3nzg4aub">
                  <Field label="Destino (envasadora) *" data-id="element-986" data-mp="2h8f2bbr5wn3v">
                    <select style={inputStyle} value={form.lineas?.[0]?.destino ?? ''} onChange={e => elegirEnvasadora(0, e.target.value)} data-id="element-987" data-mp="2m6gk7tcuqgdr">
                      <option value="" data-id="element-988" data-mp="3snljaerybtjk">
                        {envasadorasActivas.length ? '— Elige el destino —' : '— Sin envasadoras registradas —'}
                      </option>
                      {envasadorasActivas.map(env => <option key={env.id} value={env.nombre} data-id="element-989" data-mp="1gy5mz20rlaki">
                          {env.nombre}{env.ciudad ? ` · ${env.ciudad}` : ''}
                        </option>)}
                      {!!form.lineas?.[0]?.destino && !envasadorasActivas.some(env => env.nombre === form.lineas![0].destino) && <option value={form.lineas[0].destino} data-id="element-990" data-mp="1768bdu5uggl9">
                          {form.lineas[0].destino} (no registrada)
                        </option>}
                    </select>
                    {destinoActual ? <div style={{
                  marginTop: 6,
                  fontSize: 11.5,
                  color: T.textMuted
                }} data-id="element-991" data-mp="ya0pgcapalu5">
                        La cola y el camión de abajo ya salen filtrados por lo
                        que admite {destinoActual.nombre}.
                      </div> : null}
                  </Field>
                </div>}
              <Field label="Conductor del camión *" data-id="element-992" data-mp="2io12n4kwsgmv">
                <select style={inputStyle} value={form.conductor ?? ''} onChange={e => elegirConductor(e.target.value)} data-id="element-993" data-mp="38wvfbboggxi1">
                  <option value="" data-id="element-994" data-mp="33xak9ccep9al">
                    {conductoresActivos.length ? '— Elige un conductor —' : '— Sin conductores registrados —'}
                  </option>
                  {conductoresActivos.map(c => <option key={c.id} value={c.nombre} data-id="element-995" data-mp="3dpg7k8rpr2jg">
                      {c.nombre}{c.pase ? ` · ${c.pase}` : ''}
                    </option>)}
                  {/* Chofer de una orden vieja que ya no está en la lista */}
                  {conductorFuera && <option value={form.conductor} data-id="element-996" data-mp="2l1so4x26ihye">
                      {form.conductor} (no registrado)
                    </option>}
                </select>
                {!conductoresActivos.length && puedeVerFlota ? <div style={{
                marginTop: 6,
                fontSize: 11.5,
                color: T.textMuted
              }} data-id="element-997" data-mp="e901opx8slop">

                    Regístralos en <strong style={{
                  color: T.textSecondary
                }} data-id="element-998" data-mp="a752bqyvgidk">Flota y Destinos</strong> y aparecerán aquí con su
                    placa, su cola y sus galones.
                  </div> : null}
              </Field>
              <Field label="Pase del chofer" data-id="element-999" data-mp="7szyruknyaf2">
                <CampoValidado modo="codigo" valor={form.paseChofer ?? ''} onChange={v => setForm({
                ...form,
                paseChofer: v
              })} placeholder="APP-87" maxLength={15} data-id="element-1000" data-mp="7ub0loujt83j" />

              </Field>
              {/* La cola sale filtrada por lo que admite la planta. Las de
               segunda opción van en su propio grupo: se pueden usar, pero
               no son la primera elección. */}
              <Field label="Ficha de la cola" data-id="element-1001" data-mp="qprkeanoh1ma">
                {colasActivas.length ? <select style={inputStyle} value={form.fichaCola ?? ''} onChange={e => elegirCola(e.target.value)} data-id="element-1002" data-mp="vovwb91zldgb">
                    <option value="" data-id="element-1003" data-mp="1jegfnt21ycq4">— Elige la cola —</option>
                    {colasPrimera.map(c => <option key={c.id} value={c.ficha} data-id="element-1004" data-mp="1o8azqxlkktpq">
                        {c.ficha}{c.capacidadGalones ? ` · ${fmtGal(c.capacidadGalones)} gal` : ''}
                      </option>)}
                    {colasSegunda.length ? <optgroup label="Segunda opción" data-id="element-1005" data-mp="1t22j2zyaxu0o">
                        {colasSegunda.map(c => <option key={c.id} value={c.ficha} data-id="element-1006" data-mp="1p0gzkvr9ihhi">
                            {c.ficha}{c.capacidadGalones ? ` · ${fmtGal(c.capacidadGalones)} gal` : ''}
                          </option>)}
                      </optgroup> : null}
                    {/* La cola ya elegida no desaparece aunque el destino la
                     deje fuera: se muestra marcada y el aviso explica. */}
                    {!!form.fichaCola && ![...colasPrimera, ...colasSegunda].some(c => c.ficha === form.fichaCola) && <option value={form.fichaCola} data-id="element-1007" data-mp="11eajrnbljxgq">
                        {form.fichaCola} (no admitida aquí)
                      </option>}
                  </select> : <CampoValidado modo="codigo" valor={form.fichaCola ?? ''} onChange={v => setForm({
                ...form,
                fichaCola: v
              })} placeholder="58-01" maxLength={15} data-id="element-1008" data-mp="retakvx4z1g0" />}

              </Field>
              <Field label="Placa del camión *" data-id="element-1009" data-mp="37zonv9hmnwfp">
                {camionesActivos.length ? <select style={inputStyle} value={form.placaCamion ?? ''} onChange={e => setForm({
                ...form,
                placaCamion: e.target.value
              })} data-id="element-1010" data-mp="3czjb932723w0">
                    <option value="" data-id="element-1011" data-mp="2kbngw503ko2e">— Elige el camión —</option>
                    {camionesValidos.map(c => <option key={c.id} value={c.placa} data-id="element-1012" data-mp="2pa8m6iu7hxxp">
                        {c.nombre} · {c.placa}
                      </option>)}
                    {!!form.placaCamion && !camionesValidos.some(c => c.placa === form.placaCamion) && <option value={form.placaCamion} data-id="element-1013" data-mp="2y3246o11fbf9">
                        {form.placaCamion} (no permitido aquí)
                      </option>}
                  </select> : <CampoValidado modo="placa" valor={form.placaCamion ?? ''} onChange={v => setForm({
                ...form,
                placaCamion: v
              })} placeholder="L-259163" data-id="element-1014" data-mp="21j36c3o9n5ne" />}

              </Field>
            </div>


            <SectionTitle data-id="element-1015" data-mp="1hjkr7lmsr63a">
              {esWOP(form.tipo ?? '') ? 'Detalle por compartimiento' : 'Detalle del despacho'}
            </SectionTitle>

            <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            marginBottom: 18
          }} data-id="element-1016" data-mp="1w9sdkcfxd999">

              {lineas.map((linea, i) => <div key={i} style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : esWOP(form.tipo ?? '') ? '96px 1.3fr 100px 1.4fr 1fr' : '160px 1fr',
              gap: 10,
              alignItems: 'center',
              padding: '10px 12px',
              background: T.surface,
              border: `1px solid ${T.border}`,
              borderRadius: T.rMd
            }} data-id="element-1017" data-mp="256syg93scp7y" data-mpi={i}>

                  {esWOP(form.tipo ?? '') && <span style={{
                fontSize: 12,
                fontWeight: 700,
                color: T.text
              }} data-id="element-1018" data-mp="ftz67jyn6vcy">

                      Compart. {linea.compartimiento}
                    </span>}
                  {esWOP(form.tipo ?? '') && <select style={inputStyle} value={linea.producto} onChange={e => setLinea(i, {
                producto: e.target.value as ProductoWOP
              })} data-id="element-1019" data-mp="2wf2s3d8849i1">

                      {PRODUCTOS_WOP.map(prod => <option key={prod.id} value={prod.id} data-id="element-1020" data-mp="2mo83i27i8xtf">
                          {prod.label}
                        </option>)}
                    </select>}
                  {/* Los galones los pone la cola. Se dejan editables por si
                   un viaje sale con menos de lo habitual. */}
                  <Field label={esWOP(form.tipo ?? '') ? '' : 'Galones'} data-id="element-1021" data-mp="1yu87w8thx8w1">
                    <CampoValidado modo="numeros" valor={linea.galones ? String(linea.galones) : ''} onChange={v => setLinea(i, {
                  galones: Number(v) || 0
                })} placeholder="Galones" maxLength={7} data-id="element-1022" data-mp="23s00z65vn11t" />

                  </Field>

                  {/* En GLP el destino se elige arriba, una sola vez. En WOP
                   cada compartimiento puede ir a un sitio distinto. */}
                  {esWOP(form.tipo ?? '') && <select style={inputStyle} value={linea.destino} onChange={e => elegirEnvasadora(i, e.target.value)} data-id="element-1023" data-mp="3rrc2n11usum0">
                    <option value="" data-id="element-1024" data-mp="3hr1q7inn7r7w">
                      {envasadorasActivas.length ? '— Elige el destino —' : '— Sin envasadoras registradas —'}
                    </option>
                    {envasadorasActivas.map(env => <option key={env.id} value={env.nombre} data-id="element-1025" data-mp="e9jlhk3630bp">
                        {env.nombre}
                      </option>)}
                    {/* Destino escrito a mano en una orden anterior */}
                    {!!linea.destino && !envasadorasActivas.some(env => env.nombre === linea.destino) && <option value={linea.destino} data-id="element-1026" data-mp="11zdx5ybr2iud">
                        {linea.destino} (no registrada)
                      </option>}
                  </select>}

                  <Field label={esWOP(form.tipo ?? '') ? '' : 'RNC del destino'} data-id="element-1027" data-mp="x6uj7095hlp1">
                    <CampoValidado modo="rnc" valor={linea.rncDestino} onChange={v => setLinea(i, {
                  rncDestino: v
                })} placeholder="RNC del destino" data-id="element-1028" data-mp="15j612y4txke9" />

                  </Field>
                </div>)}
            </div>

            {esWOP(form.tipo ?? '') && <div style={{
            fontSize: 11,
            color: T.textMuted,
            marginBottom: 16,
            marginTop: -8
          }} data-id="element-1029" data-mp="1jidxjj9lyeer">

                Los compartimientos sin galones no se imprimen en el formulario.
              </div>}

            <Field label="Observaciones" data-id="element-1030" data-mp="1x8r69nubpx55">
              <input style={inputStyle} value={form.observaciones ?? ''} onChange={e => setForm({
              ...form,
              observaciones: e.target.value
            })} placeholder="Opcional" data-id="element-1031" data-mp="1x9sms7u3zbu9" />

            </Field>

            {/* Reglas de flota: se avisa mientras se arma la orden, sin
             esperar a que le den a guardar. */}
            {problemas.length ? <div style={{
            marginBottom: 14,
            padding: '12px 14px',
            borderRadius: 12,
            background: T.dangerSoft,
            border: `1px solid ${T.danger}33`,
            color: T.danger,
            fontSize: 12.5,
            lineHeight: 1.5
          }} data-id="element-1032" data-mp="2xgqbr948jsch">

                <strong data-id="element-1033" data-mp="31i1y95rtbxgg">Esta combinación no se puede despachar:</strong>
                <ul style={{
              margin: '6px 0 0',
              paddingLeft: 18
            }} data-id="element-1034" data-mp="2dsz9ldyrh4cg">
                  {problemas.map((p, i) => <li key={i} data-id="element-1035" data-mp="29s72aq9g99it" data-mpi={i}>{p}</li>)}
                </ul>
              </div> : null}

            {mostrarFaltantes && Object.keys(faltantes).length ? <div style={{
            marginBottom: 14,
            padding: '12px 14px',
            borderRadius: 12,
            background: T.dangerSoft,
            border: `1px solid ${T.danger}33`,
            color: T.danger,
            fontSize: 12.5,
            fontWeight: 600,
            lineHeight: 1.6
          }} data-id="element-1036" data-mp="2nk3pcfw9gt8c">
              Falta completar: {Object.values(faltantes).join(' · ')}
            </div> : null}
            <div style={{
            display: 'flex',
            gap: 10,
            justifyContent: 'flex-end'
          }} data-id="element-1037" data-mp="ac2qi9thjt8f">

              <button type="button" onClick={cerrarForm} style={ghostButton} data-id="element-1038" data-mp="3sqv8nna6002z">
                Cancelar
              </button>
              <button type="submit" style={primaryButton} data-id="element-1039" data-mp="1kahm3k40s5y">
                {editingId ? 'Guardar cambios' : 'Crear orden'}
              </button>
            </div>
          </motion.form>}
      </AnimatePresence>

      {/* ── LISTADO ── */}
      {filtrados.length === 0 ? <EmptyState title={verArchivados ? 'Sin órdenes archivadas' : 'Sin órdenes de pedido'} hint={verArchivados ? 'Las órdenes que archives aparecerán aquí y podrás devolverlas al listado.' : canEdit ? 'Crea la primera orden para imprimir el formulario de Refidomsa.' : 'Tu rol no permite crear órdenes de pedido.'} icon={<FileText size={22} data-id="element-1041" data-mp="351iwqfngddwz" />} data-id="element-1040" data-mp="fbp6gr0p7nkx" /> : <>
          <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12
        }} data-id="element-1042" data-mp="34yeq7bmim635">

            {pageItems.map(p => {
            const es = estadoStyle(p.estado);
            // Una orden archivada no se edita ni se autoriza: primero hay que
            // devolverla al listado, así no se toca sin querer algo guardado.
            const bloqueado = p.estado !== 'borrador' || !!p.archivado;
            return <motion.div key={p.id} initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} style={{
              ...glassLight,
              padding: isMobile ? 16 : '18px 20px',
              display: 'flex',
              gap: 16,
              alignItems: isMobile ? 'flex-start' : 'center',
              flexDirection: isMobile ? 'column' : 'row',
              flexWrap: 'wrap'
            }} data-id="element-1043" data-mp="2wivltrqg9pqo">

                  <div style={{
                flex: '1 1 260px',
                minWidth: 0
              }} data-id="element-1044" data-mp="2ijcpdw7042u7">

                    <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  flexWrap: 'wrap',
                  marginBottom: 6
                }} data-id="element-1045" data-mp="1yx16t4a8pf73">

                      <span style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: T.text
                  }} data-id="element-1046" data-mp="28029sw3t6q6j">

                        Orden {p.numeroOrden || '—'}
                      </span>
                      <Badge label={etiquetaTipo(p.tipo)} color={COLOR_TIPO[normalizarTipo(p.tipo)].color} soft={COLOR_TIPO[normalizarTipo(p.tipo)].soft} dot={false} data-id="element-1047" data-mp="2cxvf7mxafg5r" />

                      <Badge label={es.label} color={es.color} soft={es.soft} data-id="element-1048" data-mp="2rokt4x24v9db" />
                    </div>
                    <div style={{
                  fontSize: 12,
                  color: T.textSecondary
                }} data-id="element-1049" data-mp="2mphwo107ybgy">

                      {fmtFecha(p.fecha)} · {p.conductor || 'Sin conductor'} ·{' '}
                      {p.placaCamion || 'Sin placa'}
                    </div>
                    <div style={{
                  fontSize: 12,
                  color: T.textMuted,
                  marginTop: 3
                }} data-id="element-1050" data-mp="1t8knrvyaz0q8">

                      {p.lineas.length}{' '}
                      {p.lineas.length === 1 ? 'destino' : 'destinos'} ·{' '}
                      {fmtGal(totalGalones(p))} galones
                      {p.estado === 'autorizado' && p.autorizadoPor ? ` · Autorizó ${p.autorizadoPor}` : ''}
                    </div>
                  </div>

                  <div style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                flexWrap: 'wrap'
              }} data-id="element-1051" data-mp="2gyf1efqg4tbj">

                    <button type="button" onClick={() => imprimir(p)} style={iconBtn} title="Imprimir formulario" data-id="element-1052" data-mp="2pzmzoxsypu5m">

                      <Printer size={14} data-id="element-1053" data-mp="rn4pfh6ylaib" />
                    </button>

                    {canAutorizar && p.estado === 'borrador' && !p.archivado && <button type="button" onClick={() => autorizar(p)} style={{
                  ...iconBtn,
                  borderColor: `${T.ok}55`,
                  color: T.ok
                }} title="Autorizar orden" data-id="element-1054" data-mp="o3e24n20fsxo">

                        <ShieldCheck size={14} data-id="element-1055" data-mp="eemeus7w9wt3" />
                      </button>}

                    {canEdit && !bloqueado && <button type="button" onClick={() => abrirEdicion(p)} style={iconBtn} title="Editar" data-id="element-1056" data-mp="ad0teihaoa7a">

                        <Edit2 size={14} data-id="element-1057" data-mp="170yui43zh9n1" />
                      </button>}

                    {canArchive && p.estado === 'autorizado' && <button type="button" onClick={() => anular(p)} style={{
                  ...iconBtn,
                  borderColor: `${T.warn}55`,
                  color: T.warn
                }} title="Anular orden" data-id="element-1058" data-mp="123yjdesa1gp2">

                        <Ban size={14} data-id="element-1059" data-mp="3irt00w6cj587" />
                      </button>}

                    {canArchive && <button type="button" onClick={() => archivar(p, !p.archivado)} style={{
                  ...iconBtn,
                  borderColor: p.archivado ? `${T.info}55` : `${T.warn}55`,
                  color: p.archivado ? T.info : T.warn
                }} title={p.archivado ? 'Devolver al listado' : 'Archivar orden'} data-id="element-1060" data-mp="3ipg8psep7iih">

                        {p.archivado ? <ArchiveRestore size={14} data-id="element-1061" data-mp="wkmszk5hthcl" /> : <Archive size={14} data-id="element-1062" data-mp="wh8pu9p09443" />}
                      </button>}
                  </div>

                </motion.div>;
          })}
          </div>

          <Pagination page={page} onChange={setPage} totalPages={totalPages} rangeStart={rangeStart} rangeEnd={rangeEnd} total={total} label="órdenes" data-id="element-1063" data-mp="1oa9hinai5s6f" />

        </>}
    </PageShell>
    {dialogo}
  </>;
}